/**
 * Beacon "distance" is stored as a signal strength threshold (dBm) in
 * `distance.meters` (field name kept for schema compatibility with GPS).
 * Users pick one of three proximity levels instead of fine-tuning values.
 */
export interface BeaconProximityLevel {
  key: 'immediate' | 'near' | 'far';
  /** RSSI threshold in dBm. Closer to 0 means the user must be closer to the beacon. */
  dbm: number;
  titleId: string;
  descriptionId: string;
}

export const BEACON_PROXIMITY_LEVELS: BeaconProximityLevel[] = [
  {
    key: 'immediate',
    dbm: -50,
    titleId: 'locations.beacon.proximity.immediate',
    descriptionId: 'locations.beacon.proximity.immediate.description',
  },
  {
    key: 'near',
    dbm: -70,
    titleId: 'locations.beacon.proximity.near',
    descriptionId: 'locations.beacon.proximity.near.description',
  },
  {
    key: 'far',
    dbm: -90,
    titleId: 'locations.beacon.proximity.far',
    descriptionId: 'locations.beacon.proximity.far.description',
  },
];

export const DEFAULT_BEACON_PROXIMITY_DBM = -70; // "near"

/**
 * Returns the proximity level whose threshold is closest to the stored dBm
 * value, so locations saved with non-preset values still map to a level.
 */
export const getBeaconProximityLevel = (dbm?: number | null): BeaconProximityLevel | null => {
  if (dbm === undefined || dbm === null) return null;
  return BEACON_PROXIMITY_LEVELS.reduce((closest, level) =>
    Math.abs(level.dbm - dbm) < Math.abs(closest.dbm - dbm) ? level : closest,
  );
};
